import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowClockwise, CheckCircle, XCircle, Spinner, Clock, Terminal, IdentificationCard } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_MAP = {
  running: { label: "Ejecutando", color: "bg-amber-500 text-white", icon: Spinner },
  completed: { label: "Completado", color: "bg-[#2A9D8F] text-white", icon: CheckCircle },
  failed: { label: "Fallido", color: "bg-[#E63946] text-white", icon: XCircle },
  pending: { label: "Pendiente", color: "bg-secondary text-foreground", icon: Clock },
};

export default function ScraperMonitorPage() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobDetail, setJobDetail] = useState(null);
  const [awardsSyncing, setAwardsSyncing] = useState(false);
  const [awardsBackfilling, setAwardsBackfilling] = useState(false);
  const [backfillYear, setBackfillYear] = useState("2024");
  const logsEndRef = useRef(null);

  const runAwardsSync = async () => {
    const now = new Date();
    setAwardsSyncing(true);
    try {
      await api.post("/admin/scraper/awards-sync", { year: now.getFullYear(), month: now.getMonth() + 1 });
      toast.success(`Sincronización ${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")} iniciada en background`);
      fetchJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al iniciar sincronización");
    } finally {
      setAwardsSyncing(false);
    }
  };

  const runAwardsBackfill = async () => {
    setAwardsBackfilling(true);
    try {
      await api.post("/admin/scraper/awards-backfill", { start_year: Number(backfillYear), start_month: 1 });
      toast.success(`Backfill desde ${backfillYear}-01 iniciado en background`, {
        description: "Puede tardar varios minutos. Sigue el progreso en la lista de trabajos.",
      });
      fetchJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al iniciar backfill");
    } finally {
      setAwardsBackfilling(false);
    }
  };

  const fetchJobs = async () => {
    try {
      const res = await api.get("/scraper/jobs");
      setJobs(res.data.jobs);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchJobs(); const iv = setInterval(fetchJobs, 5000); return () => clearInterval(iv); }, []);

  useEffect(() => {
    if (selectedJob) {
      api.get(`/scraper/jobs/${selectedJob}`).then(r => setJobDetail(r.data)).catch(() => {});
    }
  }, [selectedJob, jobs]);

  useEffect(() => { logsEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [jobDetail?.logs]);

  const formatTime = (d) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleString("es-GT", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return d; }
  };

  return (
    <div data-testid="scraper-monitor-page" className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading font-black text-3xl tracking-tight">Monitor de Scraping</h1>
          <p className="text-muted-foreground text-sm mt-1">Seguimiento de trabajos de extracción en tiempo real</p>
        </div>
        <Button variant="outline" className="rounded-none" onClick={fetchJobs} data-testid="refresh-jobs">
          <ArrowClockwise size={16} className="mr-2" /> Actualizar
        </Button>
      </div>

      {/* Supplier Intelligence controls — system_admin only */}
      {user?.role === "system_admin" && (
        <Card className="rounded-none border border-border/40 shadow-none" data-testid="supplier-intel-controls">
          <CardContent className="p-4 flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 mr-2">
              <IdentificationCard size={18} className="text-[#002FA7]" />
              <div>
                <p className="font-heading font-bold text-sm leading-tight">Inteligencia de Proveedores</p>
                <p className="text-[11px] font-mono text-muted-foreground">Ingesta de adjudicaciones desde OCDS</p>
              </div>
            </div>
            <Button
              onClick={runAwardsSync}
              disabled={awardsSyncing}
              className="rounded-none bg-[#002FA7] hover:bg-[#001D66] h-9 text-xs"
              data-testid="awards-sync-current-btn"
            >
              {awardsSyncing ? <><Spinner size={14} className="mr-2 animate-spin" /> Iniciando...</> : "Sincronizar mes actual"}
            </Button>
            <div className="flex items-center gap-2">
              <Select value={backfillYear} onValueChange={setBackfillYear}>
                <SelectTrigger className="rounded-none h-9 w-28 text-xs" data-testid="backfill-year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2024">Desde 2024</SelectItem>
                  <SelectItem value="2023">Desde 2023</SelectItem>
                </SelectContent>
              </Select>
              <Button
                onClick={runAwardsBackfill}
                disabled={awardsBackfilling}
                variant="outline"
                className="rounded-none h-9 text-xs"
                data-testid="awards-backfill-btn"
              >
                {awardsBackfilling ? <><Spinner size={14} className="mr-2 animate-spin" /> Iniciando...</> : "Backfill histórico"}
              </Button>
            </div>
            <p className="text-[11px] font-mono text-muted-foreground ml-auto">
              Corre en background. Progreso visible en la lista de trabajos ↓
            </p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Jobs list */}
        <Card className="lg:col-span-2 rounded-none shadow-none border border-border/40" data-testid="jobs-list">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading font-bold text-sm">Trabajos Recientes</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[500px]">
              {loading ? (
                <div className="p-4 space-y-3">
                  {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 rounded-none" />)}
                </div>
              ) : jobs.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-sm">
                  No hay trabajos registrados
                </div>
              ) : jobs.map((job) => {
                const st = STATUS_MAP[job.status] || STATUS_MAP.pending;
                const Icon = st.icon;
                return (
                  <button
                    key={job.id}
                    data-testid={`job-item-${job.id}`}
                    className={`w-full text-left px-4 py-3 border-b border-border/40 transition-colors duration-150 hover:bg-secondary/50 ${selectedJob === job.id ? "bg-secondary" : ""}`}
                    onClick={() => setSelectedJob(job.id)}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium truncate pr-2">{job.config_name || "Sin nombre"}</span>
                      <Badge className={`rounded-none text-[10px] font-mono flex-shrink-0 ${st.color}`}>
                        <Icon size={12} className={`mr-1 ${job.status === "running" ? "animate-spin" : ""}`} weight="bold" />
                        {st.label}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="font-mono text-[10px] text-muted-foreground">{formatTime(job.started_at)}</span>
                      {job.tenders_found > 0 && (
                        <span className="font-mono text-[10px] text-[#2A9D8F]">{job.tenders_found} encontradas</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Job detail / logs */}
        <Card className="lg:col-span-3 rounded-none shadow-none border border-border/40" data-testid="job-detail">
          <CardHeader className="pb-2 border-b border-border/40">
            <CardTitle className="font-heading font-bold text-sm flex items-center gap-2">
              <Terminal size={16} weight="duotone" className="text-[#002FA7]" />
              {jobDetail ? jobDetail.config_name : "Selecciona un trabajo"}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {!jobDetail ? (
              <div className="p-8 text-center text-muted-foreground text-sm">
                Selecciona un trabajo para ver los detalles y logs
              </div>
            ) : (
              <>
                {/* Stats bar */}
                <div className="grid grid-cols-4 divide-x divide-border/40 border-b border-border/40">
                  <div className="p-3 text-center">
                    <p className="font-mono text-[10px] text-muted-foreground uppercase">Estado</p>
                    <Badge className={`rounded-none text-[10px] font-mono mt-1 ${(STATUS_MAP[jobDetail.status] || STATUS_MAP.pending).color}`}>
                      {(STATUS_MAP[jobDetail.status] || STATUS_MAP.pending).label}
                    </Badge>
                  </div>
                  <div className="p-3 text-center">
                    <p className="font-mono text-[10px] text-muted-foreground uppercase">Encontradas</p>
                    <p className="font-heading font-bold text-lg text-[#2A9D8F]">{jobDetail.tenders_found}</p>
                  </div>
                  <div className="p-3 text-center">
                    <p className="font-mono text-[10px] text-muted-foreground uppercase">Inicio</p>
                    <p className="font-mono text-xs">{formatTime(jobDetail.started_at)}</p>
                  </div>
                  <div className="p-3 text-center">
                    <p className="font-mono text-[10px] text-muted-foreground uppercase">Fin</p>
                    <p className="font-mono text-xs">{formatTime(jobDetail.completed_at)}</p>
                  </div>
                </div>

                {/* Logs terminal */}
                <div className="bg-[#09090B] text-[#00FF41] p-4 h-[360px] overflow-auto font-mono text-xs leading-relaxed" data-testid="job-logs">
                  <p className="text-white/40 mb-2">$ scraper --config {jobDetail.config_name}</p>
                  {(jobDetail.logs || []).map((log, i) => (
                    <p key={i} className="animate-fade-in-up" style={{ animationDelay: `${i * 50}ms` }}>
                      <span className="text-white/30">[{String(i + 1).padStart(3, "0")}]</span> {log}
                    </p>
                  ))}
                  {(jobDetail.errors || []).length > 0 && (
                    <>
                      <p className="text-[#E63946] mt-2">--- ERRORES ---</p>
                      {jobDetail.errors.map((err, i) => (
                        <p key={`e${i}`} className="text-[#E63946]">{err}</p>
                      ))}
                    </>
                  )}
                  <div ref={logsEndRef} />
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
